import { UpdatePinAction } from '@lessonup/pin-renderer';
import {
  getSortingQuestionAnswerOrderGenerator,
  randomId,
  SortingQuestionPinSettings,
  SortingQuestionTeacherPin,
  stringToLeanProsemirror,
} from '@lessonup/pins-shared';

const MAX_ANSWERS = 6;

export function canAddAnswer(sortingSettings: SortingQuestionPinSettings) {
  return Object.keys(sortingSettings.answers).length < MAX_ANSWERS;
}

export function addNewAnswerAction(settings: SortingQuestionTeacherPin['settings']): UpdatePinAction | null {
  if (!canAddAnswer(settings)) {
    return null;
  }

  const { answers } = settings;
  const id = randomId();

  const shuffledOrderGenerator = getSortingQuestionAnswerOrderGenerator(
    Object.values(answers).map((answer) => answer.shuffledOrder)
  );
  const correctOrderGenerator = getSortingQuestionAnswerOrderGenerator(
    Object.values(answers).map((answer) => answer.correctOrder)
  );

  // Put the new answer after a random existing answer
  const answerIds = Object.keys(answers);
  const randomAnswerId = answerIds[Math.floor(Math.random() * answerIds.length)];
  const randomShuffledOrder = randomAnswerId
    ? shuffledOrderGenerator.keyAfter(answers[randomAnswerId].shuffledOrder)
    : shuffledOrderGenerator.keyEnd();

  const newAnswers = { ...answers };
  newAnswers[id] = {
    type: 'TEXT',
    text: stringToLeanProsemirror(''),
    shuffledOrder: randomShuffledOrder,
    correctOrder: correctOrderGenerator.keyEnd(),
  };

  return {
    type: 'updatePinSettings',
    settings: {
      answers: newAnswers,
    },
  };
}

export function removeAnswerAction(answerId: string, settings: SortingQuestionPinSettings): UpdatePinAction {
  const newAnswers = { ...settings.answers };
  delete newAnswers[answerId];

  return {
    type: 'updatePinSettings',
    settings: { answers: newAnswers },
  };
}

export function reorderCorrectAnswerAction(
  answerId: string,
  newCorrectOrder: string,
  settings: SortingQuestionTeacherPin['settings']
): UpdatePinAction {
  const { answers } = settings;

  const newAnswers = {
    ...answers,
    [answerId]: {
      ...answers[answerId],
      correctOrder: newCorrectOrder,
    },
  };

  const shuffled = shuffleArray(Object.values(newAnswers).map((answer) => answer.shuffledOrder));
  Object.keys(newAnswers).forEach((answerId, index) => {
    newAnswers[answerId] = {
      ...newAnswers[answerId],
      shuffledOrder: shuffled[index],
    };
  });

  return {
    type: 'updatePinSettings',
    settings: { answers: newAnswers },
  };
}

/**
 * Creates a shuffled copy of an array without modifying the original, using the
 * Fisher-Yates algorithm.
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
